import { ApplicationAcceptedOk, EmploymentNeed, FieldValuesType, SubmitApplicationOk } from '../types';
import { CalculatorStateProps } from '../branded-consumer-loan';
import { employmentOptions } from '../hooks/use-employment-fields';

type EmploymentFields = Partial<Record<keyof Omit<EmploymentNeed, 'applicantEmploymentMonthFrom' | 'applicantEmploymentMonthUntil'>, string>>;

export const useLoanSubmit = () => {
    const sanitizeNumber = (value: string) => value?.replace(/[^\d]+/g, '');

    const extractEmploymentFields = (fields: FieldValuesType, needs: EmploymentNeed) => {
        const employmentFields: EmploymentFields = {};

        if (needs?.applicantEmploymentYearFrom) {
            const year = `${fields.applicantEmploymentYearFrom}`.slice(-2);
            employmentFields.applicantEmploymentYearFrom = `${year}${fields.applicantEmploymentMonthFrom}`;
        }

        if (needs?.applicantEmploymentYearUntil) {
            const year = `${fields.applicantEmploymentYearUntil}`.slice(-2);
            employmentFields.applicantEmploymentYearUntil = `${year}${fields.applicantEmploymentMonthUntil}`;
        }

        if (needs.applicantEmployer) {
            employmentFields.applicantEmployer = fields.applicantEmployer;
        }

        if (needs.employmentTypeQualifying) {
            employmentFields.employmentTypeQualifying = fields.employmentTypeQualifying;
        }

        return employmentFields;
    };

    function normalizeSsn(input: string | number) {
        const digits = `${input}`.replace(/\D/g, '');

        if (digits.length === 12) {
            return digits.slice(2);
        }

        return digits;
    }

    const submit = async (fields: FieldValuesType, calculator: CalculatorStateProps) => {
        try {
            const option = employmentOptions.find(option => option.value === fields.applicantEmploymentType);
            const needs = option?.needs ?? {};
            const employmentFields = extractEmploymentFields(fields, needs);

            const payload = {
                applicantGovernmentId: normalizeSsn(fields.governmentId),
                amount: calculator.loanAmount,
                time: calculator.tenorYears * 12,
                applicantMobileNumber: fields.phoneMobile,
                applicantEmailAddress: fields.contactEmail,
                purposeOfLoan: fields.purposeOfLoan,
                applicantMaritalStatus: fields.applicantMaritalStatus,
                numberChildrenNotOfAge: fields.numberChildrenNotOfAge,
                applicantHabitationForm: fields.applicantHabitationForm,
                applicantEmploymentType: fields.applicantEmploymentType,
                ...employmentFields,
                applicantMonthlyIncomeGross: fields.montlyIncomeGross,
                householdLivingExpenses: fields.livingExpenses,
                householdOtherLoanExpenses: fields.otherLoanExpenses,
                payoutAccountNumber: sanitizeNumber(`${fields.payoutAccount.accountNumber}`),
                payoutClearingNumber: sanitizeNumber(`${fields.payoutAccount.clearingNumber}`),
                insurance: fields.insurance,
                approveConditions: String(fields.approveConditions),
                agreementSignType: fields.agreementSignType,
            };

            const response = await fetch('/api/consumer-loan/submit-application', {
                method: 'POST',
                headers: {
                    'content-type': 'application/json',
                    credentials: 'include',
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                throw {
                    message: 'Något gick fel, vänligen försök igen.',
                    status: response.status,
                };
            }

            const data = await response.json();

            return data as SubmitApplicationOk;
        } catch (error) {
            console.error(error);
            throw error;
        }
    };

    const acceptQuote = async (applicationReference: string) => {
        const response = await fetch('/api/consumer-loan/accept-quote', {
            method: 'POST',
            headers: {
                'content-type': 'application/json',
                credentials: 'include',
            },
            body: JSON.stringify({ applicationReference }),
        });

        if (!response.ok) {
            throw new Error(`Accept quote failed: ${response.status}`);
        }

        const data = await response.json();
        return data as ApplicationAcceptedOk;
    };

    return { submit, acceptQuote };
};
